import { Icon } from "@iconify/react";
import { Box, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import Duration from "dayjs/plugin/duration";
import { t } from "i18next";
import { get, isEmpty, toNumber } from "lodash";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { LatLng } from "use-places-autocomplete";
import { useAuth } from "../../hooks/useAuth";
import { useUserActivityLog } from "../../hooks/useUserActivityLog";
import { TAverageChronology } from "../../shared/types/exam.type";
import { CheckPoint, FinishLine } from "../../shared/types/training.type";
import { MapColorSpeedConfig } from "../../shared/types/trainingSetting.type";
import {
  convertSpeed,
  convertStrideLength,
  getSpeedForChart,
} from "../../shared/utils/conversion";
import { getTime, startTimeFrom0 } from "../../shared/utils/MapSection";
import { RootState } from "../../store";
import BaseAccordion from "../MyTrainings/BaseAccordion";
import MapV2 from "../tracks/MapV2";
import MapControlBar from "./MapControlBar";
import MapDetails from "./MapDetails";

let colormap = require("colormap");

dayjs.extend(Duration);
type MapSectionProps = {
  transposedFile?: string;
  chronology?: TAverageChronology[];
  flIndex?: number | null;
  setDistance: (distance?: string) => void;
  onIndexChange: (index: number) => void;
  setIsLoading: (value: boolean) => void;
  isFinishLineActive: boolean;
  enableCheckbox: boolean;
  showSave: boolean;
  enabled: boolean;
  finishLine?: FinishLine;
  checkPoints?: CheckPoint[];
  children?: React.ReactNode;
  speed: number[];
  bpm: number[];
  time: number[];
  distance: number[];
  relevant_index_start?: number;
  relevant_index_end?: number;
  currentIndex: number;
  lats: number[];
  lngs: number[];
  allure: number[];
  pace: number[];
  printable?: boolean;
  strideLength: number[];
  mapMode?: "speed" | "gait";
  onMapModeChange?: (mode: "gait" | "speed") => void;
};

export type DetailInfoProps = {
  title: string;
  value?: number | string;
  icon: string;
};
const CHECKBOX_KEY = "display_section_map";

function MapSection({
  printable = false,
  pace,
  allure,
  lats,
  lngs,
  currentIndex,
  speed,
  bpm,
  distance,
  time,
  children,
  checkPoints,
  finishLine,
  enabled,
  enableCheckbox,
  chronology,
  flIndex,
  setDistance,
  onIndexChange,
  isFinishLineActive,
  strideLength = [],
  mapMode,
  onMapModeChange,
}: MapSectionProps) {
  const [detailsInfo, setDetailInfo] = useState<DetailInfoProps[]>([]);
  const [bgStr, setBgStr] = useState("");
  const metric = useSelector<RootState>(
    (state) => state.user.user?.unit_system,
  ) as "metric" | "imperial" | undefined;
  const isMapLoaded = useSelector<RootState>(
    (state) => state.display.mapLoaded,
  ) as boolean;
  const [latLngs, setLatLngs] = useState<
    (google.maps.LatLngLiteral & { color: string })[]
  >([]);
  const [play, setIsPlay] = useState(false);
  const [center, setCenter] = useState<LatLng | undefined>(undefined);
  const mapType = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.map_colors_type,
  ) as "gait" | "speed";
  const { updateUserTraingSetting, getSpeedUnit, isUserTypePolo } = useAuth();
  const map_colors_speed_configuration = useSelector<RootState>(
    (state) =>
      state.user.user?.training_settings?.map_colors_speed_configuration,
  ) as MapColorSpeedConfig;
  let id = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.id,
  ) as number;
  const { track } = useUserActivityLog();

  const colors = isUserTypePolo
    ? ["#e0e0e0", "#b3a400", "#f7911a", "#FF4433", "#99141a"]
    : ["#e0e0e0", "#b3a400", "#f7911a", "#99141a", "#FF4433"];

  const [bounds, setTotalLatLngs] = useState<
    google.maps.LatLngBounds | undefined
  >(undefined);

  const colorMapType = mapMode || mapType;

  const formattedTime = useMemo(() => {
    return startTimeFrom0(time);
  }, [time]);

  const shouldShowMap = useMemo(() => {
    return lats.filter((value) => value !== 0).length > 20;
  }, [lats]);

  //Chnage bottom scroll bar color based on color map type
  useEffect(() => {
    if (colorMapType === "gait") {
      if (chronology && chronology?.length > 0) {
        let gradients: string[] = [];
        let i = 0;

        chronology.forEach((element, index) => {
          let gr1 = `${colors[toNumber(element.gait)]} ${i}%`;
          let gr2 = `${colors[toNumber(element.gait)]} ${i + element.percentage}%`;
          i += element.percentage;
          gradients.push(`${gr1},${gr2}`);
        });
        setBgStr(`linear-gradient(90deg, ${gradients.join(",")})`);
      }
    } else {
      if (speed && speed.length > 0) {
        let gradients: string[] = [];

        speed.forEach((speed) => {
          let gr1 = getColorForSpeed(speed);
          if (gr1) {
            gradients.push(`${gr1}`);
          }
        });
        console.log({ gradients });

        setBgStr(`linear-gradient(90deg, ${gradients.join(",")})`);
      }
    }
  }, [chronology, colorMapType, speed]);

  useEffect(() => {
    calculateDistance(distance);
  }, [distance.length]);

  const calculateDistance = (distance: number[]) => {
    if (distance.length > 0) {
      let a = distance[distance.length - 1];
      let b = distance[0];

      setDistance(convertSpeed(metric, a - b));
    }
  };

  //Get total time of timestamp
  const getTimeStampLength = useCallback((): number => {
    if (!isEmpty(formattedTime)) {
      return formattedTime[formattedTime.length - 1];
    }
    return 0;
  }, [formattedTime]);

  //Get current time by index
  const getCurrentTime = () => {
    if (!isEmpty(formattedTime)) {
      return formattedTime[currentIndex];
    }
    return 0;
  };

  const getTimeStampArrLength = () => {
    return !isEmpty(formattedTime) ? formattedTime.length - 1 : 0;
  };

  useEffect(() => {
    if (isEmpty(lats) || isEmpty(lngs)) return;
    let latSum = 0;
    let lngSum = 0;

    for (let i = 0; i < lats.length; i++) {
      latSum += lats[i];
      lngSum += lngs[i];
    }

    const centerLat = latSum / lats.length;
    const centerLng = lngSum / lngs.length;

    let center = new google.maps.LatLng(centerLat, centerLng);
    setCenter({
      lat: center.lat(),
      lng: center.lng(),
    });
    if (!isEmpty(formattedTime)) {
      //Draw traject on init
      let latlngs = handleLatLngs(getTimeStampArrLength());

      const bounds = new google.maps.LatLngBounds();
      latlngs?.forEach(function (point) {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng));
      });

      setTotalLatLngs(bounds);
    }
  }, [lats, lngs, formattedTime, finishLine?.id, colorMapType]);

  const getColor = (allure: number[], speed: number[], index: number) => {
    if (colorMapType === "gait") {
      return get(colors, `[${allure[index]}]`, colors[0]);
    }
    if (colorMapType === "speed") {
      let currentSpeed = speed[index];
      return getColorForSpeed(currentSpeed);
    }
  };

  function getColorForSpeed(speed: number) {
    // var percentage = speed / map_colors_speed_configuration.max_value;
    var colors = colormap({
      colormap: "jet",
      nshades: 100,
      format: "hex",
      alpha: 1,
    });
    if (speed > map_colors_speed_configuration.max_value) return colors[99];

    var percentage = Math.floor(
      (speed /
        (map_colors_speed_configuration.max_value -
          map_colors_speed_configuration.min_value)) *
        100,
    );

    return colors[percentage];
  }

  const handleLatLngs = (index: number) => {
    let latSlice = lats.slice(0, index);
    let lngSlice = lngs.slice(0, index);
    let arr: (google.maps.LatLngLiteral & { color: string })[] = [];
    if (!isEmpty(latSlice)) {
      latSlice.forEach((lat, i) => {
        let color = getColor(allure, speed, i);
        if (lat !== 0 && lngSlice[i] !== 0) {
          arr.push({ lat, lng: lngSlice[i], color });
        }
      });

      setLatLngs(arr);
      return arr;
    }
  };

  useEffect(() => {
    handleLatLngs(currentIndex);
    onIndexChange(currentIndex);
    setDetailInfo([
      {
        title: "map:allure",
        value: allure[currentIndex]
          ? t("intervals:gait:" + allure[currentIndex])
          : " - ",
        icon: "eq-icon-2 eq-horsestep-galloping",
      },
      {
        title: "graph:vitesse",
        value:
          (getSpeedForChart(
            speed[currentIndex] ?? 0,
            getSpeedUnit()?.value ?? "",
          ) ?? "0") +
          " " +
          t(getSpeedUnit()?.key ?? ""),
        icon: "eq-icon-2 eq-horse-fullspeed",
      },
      {
        title: "graph:distance",
        value: convertSpeed(metric, distance[currentIndex]),
        icon: "eq-icon-2 eq-direction",
      },
      {
        title: "map:heartRate",
        value: (bpm[currentIndex] ? bpm[currentIndex] : 0)
          .toString()
          .concat(` ${t("units:bpm")}`),
        icon: "eq-icon-2 eq-heartbeat",
      },
      {
        title: "graph:amplitude",
        value:
          convertStrideLength(metric, strideLength[currentIndex]) +
          " " +
          t(metric === "imperial" ? "graph:pieds" : "graph:metres"),
        icon: "eq-icon-2 eq-horseshoe",
      },
    ]);
  }, [
    currentIndex,
    checkPoints,
    finishLine,
    metric,
    pace,
    strideLength,
    bpm,
    distance,
    allure,
    speed,
    colorMapType,
    isUserTypePolo,
  ]);

  //Timer for 1sec
  useEffect(() => {
    const interval = setInterval(() => {
      myCallback();
    }, 500);
    return () => clearInterval(interval);
  }, [play, currentIndex]);

  const myCallback = () => {
    if (play) {
      if (currentIndex < getTimeStampArrLength())
        onIndexChange(currentIndex + 1);
    }
  };

  const handlePlayStatus = (value: boolean) => {
    setIsPlay(value);

    //Launch event Log only if clicked play
    if (value) {
      track("TRAINING_MAP_PLAY_BUTTON_CLICKED");
    }
  };

  return (
    <BaseAccordion
      title="live.map"
      enableCheckBox={enableCheckbox}
      showInfo={false}
      showSave={false}
      defaultChecked={enabled}
      onChange={(value) => {
        updateUserTraingSetting(id, { [CHECKBOX_KEY]: value });
      }}
    >
      {shouldShowMap && isMapLoaded ? (
        <>
          {children}
          <Grid container spacing={2}>
            <Grid item md={9} xs={12} order={{ xs: 1, md: 0 }}>
              <MapV2
                center={center}
                finishline={finishLine}
                checkPoints={checkPoints}
                currentIndex={currentIndex}
                latLngs={latLngs}
                totalBound={bounds}
              />
            </Grid>
            <Grid
              sx={{ display: { md: "flex" } }}
              order={{ xs: 0, md: 1 }}
              item
              md={3}
              xs={12}
            >
              <MapDetails details={detailsInfo}></MapDetails>
            </Grid>
            {!printable && (
              <Grid order={{ xs: 2, md: 2 }} md={9} xs={12} pl={2}>
                <MapControlBar
                  isFinishLineActive={isFinishLineActive}
                  play={play}
                  changePlay={handlePlayStatus}
                  currentTime={getTime(getCurrentTime())}
                  totalTime={getTime(getTimeStampLength())}
                  max={getTimeStampArrLength()}
                  currentIndex={currentIndex}
                  onRangeChange={(val) => onIndexChange(val)}
                  flIndex={flIndex}
                  bg={bgStr}
                  mapMode={mapMode}
                  onMapModeChange={onMapModeChange}
                ></MapControlBar>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
          gap={1}
        >
          <Icon width={40} height={40} icon="hugeicons:gps-disconnected" />
          <Typography variant="subtitle2">{t("noGpsData")}</Typography>
        </Box>
      )}
    </BaseAccordion>
  );
}

export default memo(MapSection);
